import { isReactNativeApp, sendToNative } from '@mobi/web-native-comms/web'
import { addToast } from '@mobi/component-library/Feedback/Toast'
import { logError } from '@core/Utils'
import { postShareBetDataAndGenerateLink } from '@core/Areas/ShareBet/helpers/api'
import {
  isKambiSportsShareData,
  isTicketShareData,
} from '@core/Areas/ShareBet/ShareBetOutgoing/helpers/typeGuards'
import type { BetLoopShare, ShareBetData } from '@core/Areas/ShareBet/types'
import { createTicketLoadBetDetails } from '@core/Areas/ShareBet/LoadBet/createTicketLoadBetDetails'
import { createKambiCouponLoadBetDetails } from '@core/Areas/ShareBet/LoadBet/createKambiCouponLoadBetDetails'
import { trackShareBetError, extractShareContext } from '@core/Areas/ShareBet/helpers/analytics'
import type { LoadBetDetails } from '@core/Areas/ShareBet/LoadBet/types'
import { trackShareBetClick } from '../analytics'
import { createBetSlipLoadBetDetails } from './createBetSlipLoadBetDetails'
import { createImageBase64FromHtml, createImageBlobFromHtml } from './createImageFromHtml'

const enum LocalConstants {
  Title = 'My TABTouch Bet',
  Message = 'Check out this bet I placed on TABtouch 👉\n',
}

export async function handleShareBetClick({
  data,
  previewEl,
  shouldAllowLink,
  isCostShared = false,
}: {
  data: Exclude<ShareBetData, BetLoopShare>
  shouldAllowLink: boolean
  previewEl?: HTMLDivElement | null
  isCostShared?: boolean
}) {
  try {
    if (!previewEl) throw Error('Unable to create share')

    const text: ShareBetText = { title: LocalConstants.Title, message: LocalConstants.Message }

    if (isReactNativeApp) {
      await shareWithReactNative({ data, text, previewEl, shouldAllowLink })
    } else {
      await shareWithBrowser({ data, text, previewEl, shouldAllowLink })
    }

    trackShareBetClick({ data, isCostShared, destination: 'External' })
  } catch (err) {
    const errorString: string | undefined = (err as Error).toString?.().toLowerCase()
    if (errorString && (errorString.includes('cancel') || errorString.includes('abort'))) return

    logError(err as Error, true)

    const { location, betType } = extractShareContext(data)
    trackShareBetError({
      message: 'Sorry, we are unable to create your share',
      destination: 'External',
      location,
      betType,
    })

    addToast({
      id: 'share-failed',
      timeoutMs: Infinity,
      isDismissable: true,
      icon: 'error',
      title: 'Sorry, we are unable to create your share',
    })
  }
}

// =============
// Local Helpers
// =============

async function createShareBetAndPost(data: Exclude<ShareBetData, BetLoopShare>) {
  let betsToShare: LoadBetDetails[] = []

  if (isTicketShareData(data)) {
    betsToShare = createTicketLoadBetDetails(data.share.ticket)
  } else if (isKambiSportsShareData(data)) {
    betsToShare = createKambiCouponLoadBetDetails(data.share.coupon)
  } else {
    const isMulti = data.share.items.length > 1
    betsToShare = data.share.items.flatMap(item => {
      const loadBetDetails = createBetSlipLoadBetDetails(item, isMulti)
      return loadBetDetails || []
    })
  }

  const response = await postShareBetDataAndGenerateLink(betsToShare)
  if (!response.link) throw Error('ShareBet: Error ' + response.message)

  return response
}

// ============
// Native Share
// ============

async function shareWithReactNative({
  data,
  text,
  shouldAllowLink,
  previewEl,
}: ShareBetWithShared) {
  const [{ link }, base64Image] = await Promise.all([
    shouldAllowLink ? createShareBetAndPost(data) : Promise.resolve({ link: null }),
    createImageBase64FromHtml(previewEl),
  ])

  const { title, message } = text
  sendToNative('SHARE_SHEET_REQUEST', {
    url: base64Image,
    ...(shouldAllowLink && { title, subject: title, message: message + link }),
  })
}

// =============
// Browser Share
// =============

async function shareWithBrowser({ data, text, shouldAllowLink, previewEl }: ShareBetWithShared) {
  const isImageOnly = !shouldAllowLink

  const [{ link }, imageBlob] = await Promise.all([
    isImageOnly ? Promise.resolve({ link: null }) : createShareBetAndPost(data),
    isImageOnly ? createImageBlobFromHtml(previewEl) : Promise.resolve(null),
  ])

  const { title, message } = text
  const navigatorShareData: ShareData =
    isImageOnly && imageBlob
      ? {
          files: [
            new File([imageBlob], `TABtouch-share-${Date.now()}.png`, { type: imageBlob.type }),
          ],
        }
      : { title, text: message + link }

  if (navigator.canShare?.(navigatorShareData)) {
    return await navigator.share(navigatorShareData)
  }
  throw Error('No capability to share')
}

// =====
// Types
// =====

interface ShareBetWithShared {
  data: Exclude<ShareBetData, BetLoopShare>
  text: ShareBetText
  shouldAllowLink: boolean
  previewEl: HTMLDivElement
}

interface ShareBetText {
  title: string
  message: string
}
